// src/screens/s3/OneOffSpend.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 3 — One-Off Spend scenario
// Shows the true compounding cost of a large one-off purchase taken from the
// investment pot. Insight: the compounded cost exceeds the spend itself.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useState } from 'react';
import {
  ComposedChart, Area, ReferenceLine,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import { ClientData } from '../../types/ClientData';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

// Future value: lump + monthly contribution, annual rate, years.
function fv(balance: number, monthly: number, rate: number, years: number): number {
  if (years <= 0) return balance;
  const fvLump = balance * Math.pow(1 + rate, years);
  const fvMonthly = rate === 0
    ? monthly * 12 * years
    : monthly * 12 * (Math.pow(1 + rate, years) - 1) / rate;
  return fvLump + fvMonthly;
}

type SpendSource = 'isa' | 'cash' | 'pension';

export default function OneOffSpend({ data, onUpdate }: Props) {
  const clientAge = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 55;
  const minSpendAge = clientAge + 1;
  const maxSpendAge = Math.max(freedomAge - 1, minSpendAge);
  const defaultSpendAge = Math.min(maxSpendAge, Math.max(minSpendAge, clientAge + 5));

  const [spendAmount, setSpendAmount] = useState<number>(
    typeof data.s3SpendAmount === 'number' && data.s3SpendAmount > 0
      ? data.s3SpendAmount
      : 50000,
  );
  const [spendAge, setSpendAge] = useState<number>(
    data.s3SpendAge && data.s3SpendAge >= minSpendAge && data.s3SpendAge <= maxSpendAge
      ? data.s3SpendAge
      : defaultSpendAge,
  );
  const [spendSource, setSpendSource] = useState<SpendSource>(
    data.s3SpendSource ?? 'isa',
  );

  const yearsToSpend = Math.max(0, spendAge - clientAge);
  const yearsToFreedom = Math.max(0, freedomAge - clientAge);
  const yearsAfterSpend = Math.max(0, freedomAge - spendAge);

  const pensionMonthly =
    (data.pensionCompanyFunded || 0) + (data.planPensionFromDividendReduction || 0);
  const isaMonthly = data.planIsaMonthlyContribution || data.isaContribution || 0;
  const cashMonthly = 0;

  const startPension = data.pensionBalance || 0;
  const startISA = data.investmentsBalance || 0;
  const startCash =
    (data.efAccount1Balance || 0) +
    (data.efAccount2Balance || 0) +
    (data.savingsAccounts || []).reduce(
      (s, a: { balance?: number }) => s + (a.balance || 0),
      0,
    );

  const pensionRate = data.assumptionPensionGrowth || 0.08;
  const isaRate = data.assumptionIsaGrowth || 0.08;
  const cashRate = data.assumptionCashRate || 0.01;

  // Pot at spendAge per source (before deduction)
  const potAtSpendBySource = {
    pension: fv(startPension, pensionMonthly, pensionRate, yearsToSpend),
    isa: fv(startISA, isaMonthly, isaRate, yearsToSpend),
    cash: fv(startCash, cashMonthly, cashRate, yearsToSpend),
  };

  const sourceRate = spendSource === 'cash' ? cashRate : 0.08;
  const compoundingCost = spendAmount * Math.pow(1 + sourceRate, yearsAfterSpend);

  // Total full-plan pot without spend (at freedomAge)
  const potWithoutSpend =
    fv(startPension, pensionMonthly, pensionRate, yearsToFreedom) +
    fv(startISA, isaMonthly, isaRate, yearsToFreedom) +
    fv(startCash, cashMonthly, cashRate, yearsToFreedom);

  const potWithSpend = Math.max(0, potWithoutSpend - compoundingCost);

  const pensionBlocked = spendAge < 57;
  const effectiveSource: SpendSource =
    spendSource === 'pension' && pensionBlocked ? 'isa' : spendSource;
  const sufficientFunds = spendAmount <= potAtSpendBySource[effectiveSource];

  // Monthly ISA contribution to offset (neutral impact at freedomAge)
  const annuityFactor =
    yearsToFreedom === 0 || isaRate === 0
      ? 12 * Math.max(yearsToFreedom, 1)
      : 12 * (Math.pow(1 + isaRate, yearsToFreedom) - 1) / isaRate;
  const offsetMonthly = annuityFactor > 0 ? compoundingCost / annuityFactor : 0;

  // Freedom age impact (months)
  const monthlyGrowthAtFreedom = (potWithoutSpend * 0.08) / 12;
  const freedomAgeImpactMonths = monthlyGrowthAtFreedom > 0
    ? Math.round(compoundingCost / monthlyGrowthAtFreedom)
    : 0;

  const sourceName = effectiveSource === 'isa'
    ? 'ISA / investments'
    : effectiveSource === 'cash' ? 'cash savings' : 'pension';

  // Chart data
  const chartData = useMemo(() => {
    const rows: Array<{ age: number; withoutSpend: number; withSpend: number }> = [];
    for (let age = clientAge; age <= freedomAge + 2; age++) {
      const y = age - clientAge;
      const without =
        fv(startPension, pensionMonthly, pensionRate, y) +
        fv(startISA, isaMonthly, isaRate, y) +
        fv(startCash, cashMonthly, cashRate, y);
      let withS: number;
      if (age < spendAge) {
        withS = without;
      } else {
        const yrsSinceSpend = age - spendAge;
        withS = without - spendAmount * Math.pow(1 + sourceRate, yrsSinceSpend);
      }
      rows.push({
        age,
        withoutSpend: Math.round(without),
        withSpend: Math.round(Math.max(0, withS)),
      });
    }
    return rows;
  }, [clientAge, freedomAge, spendAge, spendAmount, sourceRate,
      startPension, startISA, startCash, pensionMonthly, isaMonthly, cashMonthly,
      pensionRate, isaRate, cashRate]);

  const fmt = (n: number) => `£${Math.round(n).toLocaleString()}`;
  const fmtK = (n: number) => `£${Math.round(n / 1000)}k`;

  const freedomImpactLabel =
    freedomAgeImpactMonths < 12
      ? `${freedomAgeImpactMonths} month${freedomAgeImpactMonths === 1 ? '' : 's'}`
      : `${(Math.round(freedomAgeImpactMonths / 12 * 10) / 10).toFixed(1)} years`;

  function handleSave() {
    // Persist spend inputs AND flip the scenario flag on so Programme Summary /
    // conditional nav pick it up.
    onUpdate({
      s3SpendAmount: spendAmount,
      s3SpendAge: spendAge,
      s3SpendSource: spendSource,
      s3ScenarioOneOffOn: true,
    });
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-6">
      {/* HEADER */}
      <h2 className="text-xl font-bold text-ma-navy mb-2">One-Off Spend</h2>
      <p className="text-sm text-gray-600 mb-8">
        What does a large purchase actually cost in freedom terms?
      </p>

      {/* INPUTS */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 block mb-1">
              Amount
            </label>
            <div className="flex items-baseline gap-2">
              <span className="text-gray-400">£</span>
              <input
                type="number"
                min={0}
                step={1000}
                value={spendAmount || ''}
                onChange={(e) =>
                  setSpendAmount(e.target.value === '' ? 0 : Number(e.target.value))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-lg font-semibold text-ma-navy"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 block mb-1">
              At age
            </label>
            <input
              type="number"
              min={minSpendAge}
              max={maxSpendAge}
              value={spendAge || ''}
              onChange={(e) =>
                setSpendAge(e.target.value === '' ? minSpendAge : Number(e.target.value))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-lg font-semibold text-ma-navy"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 block mb-1">
              From
            </label>
            <select
              value={spendSource}
              onChange={(e) => setSpendSource(e.target.value as SpendSource)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-ma-navy"
            >
              <option value="isa">ISA / Investments</option>
              <option value="cash">Cash savings</option>
              <option value="pension" disabled={pensionBlocked}>
                Pension (age 57+ only)
              </option>
            </select>
          </div>
        </div>
      </div>

      {/* VALIDATION WARNING */}
      {!sufficientFunds && (
        <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-5">
          <p className="text-sm text-amber-900">
            At age {spendAge}, your {sourceName} is projected to be{' '}
            {fmt(potAtSpendBySource[effectiveSource])} — not enough to cover{' '}
            {fmt(spendAmount)}. Reduce the amount or choose a different source.
          </p>
        </div>
      )}

      {/* INSIGHT CARDS */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-red-800 mb-1">
            Compounding cost
          </div>
          <div className="text-3xl font-bold text-red-900">{fmt(compoundingCost)}</div>
          <p className="text-xs text-red-800 mt-2">
            What {fmt(spendAmount)} at age {spendAge} actually costs at your Freedom
            Number age — compounded at {(sourceRate * 100).toFixed(0)}% for{' '}
            {yearsAfterSpend} year{yearsAfterSpend === 1 ? '' : 's'}.
          </p>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-1">
            Freedom age impact
          </div>
          <div className="text-3xl font-bold text-amber-900">{freedomImpactLabel}</div>
          <p className="text-xs text-amber-800 mt-2">
            How much later you reach your Freedom Number.
          </p>
        </div>
      </div>

      {/* CHART */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
          With vs without the spend
        </h3>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="age" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} stroke="#9CA3AF" />
              <Tooltip
                formatter={(v: any) => fmt(Number(v))}
                labelFormatter={(age) => `Age ${age}`}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                dataKey="withoutSpend"
                name="Without spend"
                fill="#1E3A5F"
                fillOpacity={0.1}
                stroke="#1E3A5F"
                strokeWidth={2}
              />
              <Area
                dataKey="withSpend"
                name="With spend"
                fill="#EF4444"
                fillOpacity={0.1}
                stroke="#EF4444"
                strokeWidth={2}
              />
              <ReferenceLine
                y={data.freedomNumberInflated || 0}
                stroke="#C9A84C"
                strokeDasharray="4 4"
                label={{ value: 'Freedom Number', fontSize: 10, fill: '#C9A84C', position: 'insideTopLeft' }}
              />
              <ReferenceLine
                x={spendAge}
                stroke="#9CA3AF"
                strokeDasharray="3 3"
                label={{ value: `Age ${spendAge} — spend`, fontSize: 11, position: 'top' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* OFFSET */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <p className="text-sm text-gray-600 mb-2">
          To absorb this spend with zero impact on your freedom age:
        </p>
        <div className="rounded-xl border border-ma-navy/20 bg-ma-navy/5 p-4">
          <div className="text-2xl font-bold text-ma-navy">
            {fmt(offsetMonthly)}/month
          </div>
          <p className="text-xs text-ma-navy/80 mt-1">
            Extra ISA contribution from today. Start now and this spend costs
            you nothing in freedom terms.
          </p>
        </div>
      </div>

      {/* PLAIN ENGLISH */}
      <div className="bg-ma-navy text-white rounded-2xl p-6 mb-6">
        {sufficientFunds ? (
          <p className="text-sm leading-relaxed">
            A <strong>{fmt(spendAmount)}</strong> spend from {sourceName} at age{' '}
            {spendAge} doesn't just cost {fmt(spendAmount)} — it costs{' '}
            <strong>{fmt(compoundingCost)}</strong> in freedom terms, because
            that money would have compounded at {(sourceRate * 100).toFixed(0)}%
            for {yearsAfterSpend} year{yearsAfterSpend === 1 ? '' : 's'}. The
            freedom age impact is <strong>{freedomImpactLabel}</strong>. An
            extra <strong>{fmt(offsetMonthly)}/month</strong> ISA contribution
            from today absorbs it entirely.
          </p>
        ) : (
          <p className="text-sm leading-relaxed">
            The pot won't support this spend at age {spendAge}. Adjust the
            amount or age.
          </p>
        )}
      </div>

      {/* VOYANT BREAK */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-blue-800 mb-1">
          Voyant break — adviser check
        </div>
        <p className="text-sm text-blue-900 leading-relaxed">
          In Voyant, model a {fmt(spendAmount)} withdrawal from {sourceName} at
          age {spendAge}. Confirm the plan still crosses the Freedom Number and
          is funded through 82.
        </p>
      </div>

      {/* Silent guard so potWithSpend is referenced (future: show as chart end-cap) */}
      <div className="sr-only" aria-hidden="true">
        End pot {potWithSpend}
      </div>

      {/* SAVE */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="px-5 py-2.5 rounded-lg bg-ma-navy text-white text-sm font-semibold hover:bg-opacity-90 transition"
        >
          Include in scenarios
        </button>
      </div>
    </div>
  );
}
